import React, { useEffect, useMemo, useState } from "react";
import { Button, Empty, Segmented, Select, Space, Spin, Table, Tag, Tooltip, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import HeaderHelp, { AIRING_COLUMN_HELP, SLOT_COLUMN_HELP, SlotsHelp } from "./HeaderHelp";
import { SlotGridOption, SlotRow, downloadSlotsCsv, fetchSlotOptions, fetchSlots } from "./api";

const { Text } = Typography;

const DAYS = [
  { dow: 1, label: "Mon" },
  { dow: 2, label: "Tue" },
  { dow: 3, label: "Wed" },
  { dow: 4, label: "Thu" },
  { dow: 5, label: "Fri" },
  { dow: 6, label: "Sat" },
  { dow: 7, label: "Sun" },
];
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const hh = (h: number) => `${String(h).padStart(2, "0")}:00`;
const pct = (v: number) => (v >= 0 ? "+" : "") + v.toFixed(1) + "%";
const slotLabel = (r: SlotRow) => `${DAYS[r.dayOfWeek - 1]?.label ?? r.dayName} ${hh(r.hourOfDay)}`;

// The platform the worked examples in the docs use, and the most reliable one so far.
const PREFERRED_PLATFORM = "LG TV - US";
const PREFERRED_CHANNEL = "Nosey INTL 1";

/**
 * Diverging colour steps: blue below average, red above, gray within 5% of it. The cut points are
 * the same on every platform so two grids can be compared. Each arm is one hue stepped light to
 * dark; the two darkest steps take white text, the rest dark text.
 */
const INK = "#1f1f1f";
const NEUTRAL = "#f0efec";
const STEPS = [
  { from: 5, below: "#cde2fb", above: "#fad6d2", ink: INK },
  { from: 15, below: "#9ec5f4", above: "#f1aea8", ink: INK },
  { from: 30, below: "#6da7ec", above: "#e4857e", ink: INK },
  { from: 50, below: "#2a78d6", above: "#c74845", ink: "#fff" },
  { from: 75, below: "#184f95", above: "#892b2a", ink: "#fff" },
];

const cellColors = (pctDiff: number) => {
  const magnitude = Math.abs(pctDiff);
  const step = [...STEPS].reverse().find((s) => magnitude >= s.from);
  if (!step) return { background: NEUTRAL, color: INK };
  return { background: pctDiff < 0 ? step.below : step.above, color: step.ink };
};

const Grid = styled.table`
  border-collapse: separate;
  border-spacing: 2px;
  table-layout: fixed;
  th {
    font-weight: 500;
    color: #595959;
    font-size: 12px;
    padding: 2px 4px;
    text-align: center;
    width: 62px;
  }
  th.hour {
    text-align: right;
    padding-right: 8px;
    width: 52px;
  }
  td {
    height: 24px;
    border-radius: 3px;
    text-align: center;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    cursor: default;
    padding: 0 4px;
  }
  td.thin {
    opacity: 0.55;
    outline: 1px dashed #8c8c8c;
    outline-offset: -1px;
  }
  td:hover {
    outline: 2px solid ${INK};
    outline-offset: -2px;
    opacity: 1;
  }
  td.empty {
    background: repeating-linear-gradient(45deg, #fafafa, #fafafa 3px, #eeeeee 3px, #eeeeee 6px);
  }
`;

const Legend: React.FC = () => {
  const swatches = [...[...STEPS].reverse().map((s) => s.below), NEUTRAL, ...STEPS.map((s) => s.above)];
  const bounds = [...[...STEPS].reverse().map((s) => `-${s.from}`), ...STEPS.map((s) => `+${s.from}`)];
  return (
    <div style={{ fontSize: 11, color: "#595959" }}>
      <div style={{ marginBottom: 4 }}>% vs an average slot on this platform</div>
      <div style={{ display: "flex", gap: 2 }}>
        {swatches.map((color, i) => (
          <div key={i} style={{ width: 38, height: 14, background: color, borderRadius: 2 }} />
        ))}
      </div>
      <div style={{ display: "flex", marginLeft: 19, marginTop: 2 }}>
        {bounds.map((b) => (
          <div key={b} style={{ width: 40, textAlign: "center" }}>
            {b}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
        <div style={{ width: 24, height: 14, background: NEUTRAL, opacity: 0.55, outline: "1px dashed #8c8c8c", outlineOffset: -1, borderRadius: 2 }} />
        thin: under 8 airings
      </div>
    </div>
  );
};

const CellDetail: React.FC<{ row: SlotRow }> = ({ row }) => (
  <div style={{ fontSize: 12, lineHeight: 1.6 }}>
    <div>
      <Text strong style={{ color: "#fff", fontSize: 14 }}>
        {pct(row.slotPctDiff)}
      </Text>{" "}
      vs an average slot{row.confidence === "thin" ? ", thin" : ""}
    </div>
    {row.isOutlier && row.neighbourGap != null && (
      <div>
        Outlier: {Math.abs(row.neighbourGap).toFixed(0)} points {row.neighbourGap >= 0 ? "above" : "below"} the hours around it
      </div>
    )}
    <div>
      {slotLabel(row)} ({row.localTz})
    </div>
    <div>
      Rate {row.rawMeanRate.toFixed(2)} HOV/min over {row.airings} airings, {row.seriesCount} series
    </div>
    <div>
      Aired {row.firstAirDate} to {row.lastAirDate}
    </div>
  </div>
);

/** Ten slots, strongest or weakest first, beside the grid. */
const RankedSlots: React.FC<{ title: string; rows: SlotRow[] }> = ({ title, rows }) => (
  <Table
    size="small"
    rowKey={(r) => `${r.dayOfWeek}|${r.hourOfDay}`}
    dataSource={rows}
    pagination={false}
    title={() => <Text strong>{title}</Text>}
    style={{ width: 300 }}
    columns={[
      { title: "Slot", key: "slot", render: (_: unknown, r: SlotRow) => slotLabel(r) },
      { title: "Vs average", dataIndex: "slotPctDiff", align: "right", render: (v: number) => pct(v) },
      { title: "Airings", dataIndex: "airings", align: "right", render: (v: number, r: SlotRow) => (r.confidence === "thin" ? <Text type="secondary">{v} (thin)</Text> : v) },
    ]}
  />
);

/**
 * Slot strength: the weekday-by-hour grid of EPISODE_SCORING_SLOT_BASELINES for one platform and
 * channel, drawn the way the original Jerry/Steve analysis drew it, plus the ten strongest and
 * weakest slots and a sortable table view.
 */
const SlotsTab: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const [options, setOptions] = useState<SlotGridOption[] | null>(null);
  const [platform, setPlatform] = useState<string>();
  const [channel, setChannel] = useState<string>();
  const [rows, setRows] = useState<SlotRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<"grid" | "table">("grid");

  useEffect(() => {
    fetchSlotOptions().then((data) => {
      setOptions(data.rows);
      setPlatform((current) => {
        const platforms = data.rows.map((o) => o.platform);
        if (current && platforms.includes(current)) return current;
        return platforms.includes(PREFERRED_PLATFORM) ? PREFERRED_PLATFORM : platforms[0];
      });
    });
  }, [refreshKey]);

  const platforms = useMemo(() => Array.from(new Set((options || []).map((o) => o.platform))), [options]);
  const channels = useMemo(() => (options || []).filter((o) => o.platform === platform).map((o) => o.channel), [options, platform]);

  useEffect(() => {
    if (!channels.length) return;
    setChannel((current) => {
      if (current && channels.includes(current)) return current;
      return channels.includes(PREFERRED_CHANNEL) ? PREFERRED_CHANNEL : channels[0];
    });
  }, [channels]);

  useEffect(() => {
    if (!platform || !channel) return;
    setLoading(true);
    fetchSlots(platform, channel)
      .then((data) => setRows(data.rows))
      .finally(() => setLoading(false));
  }, [platform, channel, refreshKey]);

  const selected = (options || []).find((o) => o.platform === platform && o.channel === channel);
  const byKey = useMemo(() => new Map(rows.map((r) => [`${r.dayOfWeek}|${r.hourOfDay}`, r])), [rows]);
  const ranked = useMemo(() => [...rows].sort((a, b) => b.slotPctDiff - a.slotPctDiff), [rows]);
  const firstAir = rows.reduce<string | null>((min, r) => (min === null || r.firstAirDate < min ? r.firstAirDate : min), null);
  const lastAir = rows.reduce<string | null>((max, r) => (max === null || r.lastAirDate > max ? r.lastAirDate : max), null);

  if (options && options.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="No slot baselines yet. Upload a report on the Upload tab." />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space wrap style={{ marginBottom: 16 }}>
        <SlotsHelp />
        <Select
          showSearch
          placeholder="Platform"
          value={platform}
          style={{ width: 230 }}
          onChange={setPlatform}
          options={platforms.map((p) => ({ value: p, label: p }))}
        />
        <Select placeholder="Channel" value={channel} style={{ width: 180 }} onChange={setChannel} options={channels.map((c) => ({ value: c, label: c }))} />
        <Segmented
          value={view}
          onChange={(v) => setView(v as "grid" | "table")}
          options={[
            { label: "Grid", value: "grid" },
            { label: "Table", value: "table" },
          ]}
        />
        <Button icon={<DownloadOutlined />} disabled={!platform || !channel} onClick={() => platform && channel && downloadSlotsCsv(platform, channel)}>
          Download CSV
        </Button>
        {selected && (
          <Text type="secondary">
            Hours in {selected.localTz}.{firstAir && lastAir ? ` Airings ${firstAir} to ${lastAir}` : ""}
            {selected.computedAt ? `, computed ${new Date(selected.computedAt).toLocaleString()}` : ""}
          </Text>
        )}
      </Space>

      <Spin spinning={loading}>
        {view === "grid" ? (
          <div style={{ display: "flex", gap: 32, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div>
              <Grid>
                <thead>
                  <tr>
                    <th className="hour">Hour</th>
                    {DAYS.map((d) => (
                      <th key={d.dow}>{d.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {HOURS.map((h) => (
                    <tr key={h}>
                      <th className="hour">{hh(h)}</th>
                      {DAYS.map((d) => {
                        const row = byKey.get(`${d.dow}|${h}`);
                        if (!row) return <td key={d.dow} className="empty" />;
                        const colors = cellColors(row.slotPctDiff);
                        return (
                          <Tooltip key={d.dow} title={<CellDetail row={row} />} mouseEnterDelay={0.05}>
                            <td className={row.confidence === "thin" ? "thin" : undefined} style={colors}>
                              {pct(row.slotPctDiff).replace("%", "")}
                            </td>
                          </Tooltip>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Grid>
              <div style={{ marginTop: 16 }}>
                <Legend />
              </div>
            </div>
            {rows.length > 0 && (
              <Space align="start" size={24} wrap>
                <RankedSlots title="Strongest 10 slots" rows={ranked.slice(0, 10)} />
                <RankedSlots title="Weakest 10 slots" rows={ranked.slice(-10).reverse()} />
              </Space>
            )}
          </div>
        ) : (
          <Table
            size="small"
            rowKey={(r) => `${r.dayOfWeek}|${r.hourOfDay}`}
            dataSource={rows}
            pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: [24, 50, 100, 168] }}
            scroll={{ x: 1230 }}
            columns={[
              { title: "Day", dataIndex: "dayName", width: 110, sorter: (a, b) => a.dayOfWeek - b.dayOfWeek },
              { title: "Hour", dataIndex: "hourOfDay", width: 80, render: hh, sorter: (a, b) => a.hourOfDay - b.hourOfDay },
              {
                title: <HeaderHelp label="Slot vs average" help={AIRING_COLUMN_HELP.slotVsAverage} />,
                dataIndex: "slotPctDiff",
                width: 150,
                align: "right",
                render: (v: number) => <Text type={v >= 0 ? "success" : "danger"}>{pct(v)}</Text>,
                sorter: (a, b) => a.slotPctDiff - b.slotPctDiff,
              },
              { title: <HeaderHelp label="Rate" help={SLOT_COLUMN_HELP.rawMeanRate} />, dataIndex: "rawMeanRate", width: 100, align: "right", render: (v: number) => v.toFixed(2), sorter: (a, b) => a.rawMeanRate - b.rawMeanRate },
              { title: <HeaderHelp label="Airings" help={SLOT_COLUMN_HELP.airings} />, dataIndex: "airings", width: 110, align: "right", sorter: (a, b) => a.airings - b.airings },
              { title: <HeaderHelp label="Series" help={SLOT_COLUMN_HELP.seriesCount} />, dataIndex: "seriesCount", width: 100, align: "right", sorter: (a, b) => a.seriesCount - b.seriesCount },
              { title: <HeaderHelp label="Confidence" help={SLOT_COLUMN_HELP.confidence} />, dataIndex: "confidence", width: 130, align: "center" },
              { title: <HeaderHelp label="Z" help={SLOT_COLUMN_HELP.slotZ} />, dataIndex: "slotZ", width: 90, align: "right", render: (v: number) => v.toFixed(2), sorter: (a, b) => a.slotZ - b.slotZ },
              {
                title: <HeaderHelp label="Vs neighbours" help={SLOT_COLUMN_HELP.neighbourGap} />,
                dataIndex: "neighbourGap",
                width: 130,
                align: "right",
                render: (v: number | null) => (v == null ? "" : (v >= 0 ? "+" : "") + v.toFixed(1)),
                sorter: (a, b) => Math.abs(a.neighbourGap ?? 0) - Math.abs(b.neighbourGap ?? 0),
              },
              { title: <HeaderHelp label="Outlier" help={SLOT_COLUMN_HELP.outlier} />, dataIndex: "isOutlier", width: 110, align: "center", render: (v: boolean) => (v ? <Tag color="gold">outlier</Tag> : "") },
              { title: "First aired", dataIndex: "firstAirDate", width: 120 },
              { title: "Last aired", dataIndex: "lastAirDate", width: 120 },
            ]}
          />
        )}
      </Spin>
    </div>
  );
};

export default SlotsTab;
